import {
  Button,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateDrugRequest,
  Drug,
  UpdateDrugRequest,
} from '@interfaces/drugs.interface';

import { useCreateDrugMutation, useUpdateDrugMutation } from '@api/drugs.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_DRUG_FORM_MODAL_ID = 'upsertDrug';

interface UpsertDrugFormProps {
  drug?: Drug;
}

export default function UpsertDrugForm({ drug }: UpsertDrugFormProps) {
  const initialValues = {
    aic: drug?.aic || '',
    aicNameHolder: drug?.aicNameHolder || '',
    name: drug?.name || '',
    activeIngredient: drug?.activeIngredient || '',
    //// measureUnit: drug?.measureUnit || '',
    retailPrice: drug?.retailPrice || undefined,
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['name'],
      //// lowercaseFields: ['measureUnit'],
    },
  });

  const [createDrug, { isLoading: isLoadingCreate }] = useCreateDrugMutation();
  const [updateDrug, { isLoading: isLoadingUpdate }] = useUpdateDrugMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_DRUG_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (drug) {
        const data: UpdateDrugRequest = {
          aic: drug.aic,
          body: {
            aic: values.aic,
            aicNameHolder: values.aicNameHolder,
            name: values.name,
            activeIngredient: values.activeIngredient,
            //// measureUnit: values.measureUnit,
            retailPrice: values.retailPrice,
          },
        };

        await updateDrug(data).unwrap();

        notifications.show({
          title: 'Farmaco modificato',
          message: 'Farmaco modificato con successo.',
        });
      } else {
        const data: CreateDrugRequest = {
          aic: values.aic,
          aicNameHolder: values.aicNameHolder,
          name: values.name,
          activeIngredient: values.activeIngredient,
          //// measureUnit: values.measureUnit,
          retailPrice: values.retailPrice!,
        };

        await createDrug(data).unwrap();

        notifications.show({
          title: 'Farmaco creato',
          message: 'Farmaco creato con successo.',
        });
      }

      closeModal(UPSERT_DRUG_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="AIC"
          {...form.getInputProps('aic')}
        />
        <TextInput
          required
          label="Titolare AIC"
          {...form.getInputProps('aicNameHolder')}
        />
        <TextInput required label="Nome" {...form.getInputProps('name')} />
        <TextInput
          required
          label="Principio attivo"
          {...form.getInputProps('activeIngredient')}
        />
        {/* <TextInput
          required
          label="Unità di misura"
          {...form.getInputProps('measureUnit')}
        /> */}
        <NumberInput
          required
          label="Prezzo al dettaglio"
          prefix="€ "
          {...form.getInputProps('retailPrice')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
